import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProtectedRoute } from "@/components/site/ProtectedRoute";
import { ErrorBoundary } from "@/components/site/ErrorBoundary";
import { CallProvider } from "@/context/CallContext";
import { CallWindow } from "@/components/site/CallWindow";
import Landing from "@/pages/Landing";
import CoursePage from "@/pages/CoursePage";
import AuthPage from "@/pages/AuthPage";
import ApplyPage from "@/pages/ApplyPage";
import StudentDashboard from "@/pages/StudentDashboard";
import LessonPage from "@/pages/LessonPage";
import PrintPage from "@/pages/PrintPage";
import HostDashboard from "@/pages/HostDashboard";
import HostPerson from "@/pages/HostPerson";
import NotFound from "@/pages/NotFound";
import NicaraguaPage from "@/pages/NicaraguaPage";
import GregMiceliPage from "@/pages/GregMiceliPage";
import AboutHostPage from "@/pages/AboutHostPage";
import ContactPage from "@/pages/ContactPage";
import AiCoursePage from "@/pages/AiCoursePage";
import CertificatePage from "@/pages/CertificatePage";
import GraduatesPage from "@/pages/GraduatesPage";
import { BlogPage, BlogPostPage } from "@/pages/BlogPages";
import ClassPage from "@/pages/ClassPage";
import BookCallPage from "@/pages/BookCallPage";
import TermsPage from "@/pages/TermsPage";
import RetreatsPage from "@/pages/RetreatsPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import CommunityPage from "@/pages/CommunityPage";
import OnlineCoursesPage from "@/pages/OnlineCoursesPage";
import AvenueCoursePage from "@/pages/AvenueCoursePage";
import AvenueLessonPage from "@/pages/AvenueLessonPage";
import AvenueWorksheetPage from "@/pages/AvenueWorksheetPage";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { AnalyticsTracker } from "@/lib/analytics";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isPrint = pathname.startsWith("/print");
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollTop />
      <AnalyticsTracker />
      {!isPrint && <Header />}
      <main className="flex-1">
        <ErrorBoundary key={pathname}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/digital-nomad-beginners-course" element={<CoursePage />} />
          <Route path="/ai-website-developer-course" element={<AiCoursePage />} />
          <Route path="/nicaragua" element={<NicaraguaPage />} />
          <Route path="/greg-miceli" element={<GregMiceliPage />} />
          <Route path="/about-the-host" element={<AboutHostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/book-a-call" element={<BookCallPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/retreats" element={<RetreatsPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/online-courses" element={<OnlineCoursesPage />} />
          <Route path="/online-courses/:slug" element={<AvenueCoursePage />} />
          <Route path="/learn/:slug/worksheet/:wid" element={<ProtectedRoute><AvenueWorksheetPage /></ProtectedRoute>} />
          <Route path="/host/teach/:userId/learn/:slug/worksheet/:wid" element={<ProtectedRoute host><AvenueWorksheetPage /></ProtectedRoute>} />
          <Route path="/learn/:slug/:part" element={<ProtectedRoute><AvenueLessonPage /></ProtectedRoute>} />
          <Route path="/host/teach/:userId/learn/:slug/:part" element={<ProtectedRoute host><AvenueLessonPage /></ProtectedRoute>} />
          <Route path="/classes/:slug" element={<ClassPage />} />
          <Route path="/graduates" element={<GraduatesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/signup" element={<AuthPage mode="signup" />} />
          <Route path="/apply" element={<ProtectedRoute><ApplyPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
          <Route path="/course/day/:day" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
          <Route path="/print/:kind" element={<PrintPage />} />
          <Route path="/print/:kind/:day" element={<PrintPage />} />
          <Route path="/host" element={<ProtectedRoute host><HostDashboard /></ProtectedRoute>} />
          <Route path="/host/people/:email" element={<ProtectedRoute host><HostPerson /></ProtectedRoute>} />
          <Route path="/host/teach/:userId/day/:day" element={<ProtectedRoute host><LessonPage /></ProtectedRoute>} />
          <Route path="/certificate/:id" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
          <Route path="/verify/:id" element={<CertificatePage verify />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
      </main>
      {!isPrint && <Footer />}
      {!isPrint && <WhatsAppFloat />}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <Shell />
            <CallWindow />
          </CallProvider>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
